import { FC, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@mui/material';
import { useCSVDownloader } from 'react-papaparse';
import { Row } from '../StateBasedPricing/StateBasedPricing';
import { useGetAllPriceListsQuery } from '@services';
import { CsvProduct } from '../ProductCsvReader/ProductCsvReader';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  rows: Row[];
};

export const StateBasedPricingCsvDownloadModal: FC<Props> = ({ isOpen, onClose, rows }) => {
  const { data: priceLists } = useGetAllPriceListsQuery();
  const stateNames = priceLists?.map((priceList) => priceList.name.toLowerCase()) || [];
  const stateBasedPricingHeaderArray = ['sku', 'name', 'price', ...stateNames];
  const [includedHeaders, setIncludedHeaders] = useState(stateBasedPricingHeaderArray);

  const { CSVDownloader } = useCSVDownloader();

  const handleChange = (header: string) => {
    if (includedHeaders.includes(header)) {
      setIncludedHeaders(includedHeaders.filter((h) => h !== header));
    } else {
      setIncludedHeaders([...includedHeaders, header]);
    }
  };

  const getCsvData = (): CsvProduct[] => {
    return rows.map((row) => {
      const csvProductStatePricing: CsvProduct = {};

      includedHeaders.forEach((header) => (csvProductStatePricing[header] = row[header]));

      return csvProductStatePricing;
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md">
      <DialogTitle>Download CSV</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', pb: 0, mt: 2 }}>
        <FormControl>
          <FormLabel component="legend">Fields to include</FormLabel>
          <FormGroup sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            {stateBasedPricingHeaderArray.map((header) => (
              <FormControlLabel
                control={<Checkbox checked={includedHeaders.includes(header)} onChange={() => handleChange(header)} />}
                key={header}
                label={header}
                sx={{ width: '25%' }}
              />
            ))}
          </FormGroup>
        </FormControl>
        <Box>
          <Button onClick={() => setIncludedHeaders([])} data-testid="state-pricing-download-unselect-all-btn">
            Unselect all
          </Button>
          <Button
            onClick={() => setIncludedHeaders(stateBasedPricingHeaderArray)}
            sx={{ ml: 1 }}
            data-testid="state-pricing-download-select-all-btn"
          >
            Select all
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', my: 2, px: 3 }}>
        <Box>
          <Button type="button" variant="outlined" onClick={onClose} data-testid="state-pricing-download-cancel-btn">
            Cancel
          </Button>
          <CSVDownloader data={() => getCsvData()} filename={`jimbo-state-based-pricing-export-${new Date().toISOString()}`}>
            <Button sx={{ ml: 2 }} variant="contained" data-testid="state-pricing-download-btn">
              Download
            </Button>
          </CSVDownloader>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
